import { AppointmentModel } from "@devexpress/dx-react-scheduler";
import { AppointmentTooltip as DXAppointmentTooltip } from "@devexpress/dx-react-scheduler-material-ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo } from "react";
import * as React from "react";

import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grid from "@mui/material/Grid";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";

import { useAppointmentOutcome, useScheduleNextAppointment } from "hooks";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  asksCancellationMessage,
  withCancellationMessageChoice
} from "components/cancellation-message-choice";
import {
  setOpen as setConfirmChangeDialogOpen,
  setContent,
  setOnChange,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import {
  APPOINTMENT_STATUS_ARRIVED,
  APPOINTMENT_STATUS_CANCELED,
  APPOINTMENT_STATUS_COMPLETED,
  APPOINTMENT_STATUS_CONFIRMED,
  APPOINTMENT_STATUS_NO_SHOW,
  APPOINTMENT_STATUS_RESCHEDULED,
  APPOINTMENT_STATUS_UNCONFIRMED
} from "constants/appointments";
import {
  NEW_APPOINTMENT_OUTCOME_CHANGES,
  OPEN_PATIENT_ON_APPOINTMENT_STATUS_CHANGE
} from "constants/feature-flags";
import { selectCurrentPractice } from "features/auth/auth-slice";
import useAppointments from "features/scheduling/hooks/use-appointments.hook";
import {
  endRescheduling,
  hidePreview,
  startRescheduling
} from "features/scheduling/scheduler-slice";
import { getIsAppointmentDateInTheFuture } from "features/scheduling/utils/scheduling.utils";
import {
  STATUS_CONFIRMATION_OPTIONS,
  STATUS_TRANSITION_OPTIONS,
  getStatusTransition,
  isDraftPendingPatientVerification
} from "features/scheduling/utils/status-transitions.util";
import { useUndeliveredDevicesCheck } from "hooks/use-undelivered-devices-check";

import useStaffMemberSchedule from "../../use-staff-member-schedule.hook";
import StatusTransitionButtonGroup from "./status-transition-button-group.component";

const openPatientChartOnArrival = (
  shouldOpen: boolean,
  nextStatus: number,
  patientId: string | undefined
): void => {
  if (!shouldOpen || nextStatus !== APPOINTMENT_STATUS_ARRIVED || !patientId) {
    return;
  }
  window.open(`/patients/${patientId}/chart-notes`, "_blank");
};

type AppointmentPreviewActionProps = {
  appointmentData: DXAppointmentTooltip.ContentProps["appointmentData"] &
    AppointmentModel;
  canAppointmentBeCompleted: boolean;
  hasCustomChecklist: boolean;
};

export default function AppointmentPreviewAction({
  appointmentData,
  canAppointmentBeCompleted,
  hasCustomChecklist
}: AppointmentPreviewActionProps) {
  const flags = useFlags();

  const { updateAppointmentStatus, setupAppointmentModal } = useAppointments();
  const dispatch = useAppDispatch();
  const { initAppointmentOutcomeDialog } = useAppointmentOutcome();
  const { checkUndeliveredDevices } = useUndeliveredDevicesCheck();
  const { initScheduleNextAppointment } = useScheduleNextAppointment();

  const openPatientOnAppointmentStatusChange =
    flags[OPEN_PATIENT_ON_APPOINTMENT_STATUS_CHANGE];
  const newAppointmentOutcomeChangesEnabled =
    flags[NEW_APPOINTMENT_OUTCOME_CHANGES];

  const anchorRef = React.useRef<HTMLDivElement>(null);

  const updateStatus = updateAppointmentStatus(
    initAppointmentOutcomeDialog,
    checkUndeliveredDevices,
    initScheduleNextAppointment
  );

  const { isAvailable } = useStaffMemberSchedule({
    staffMemberId: appointmentData.staffMember?.id || null,
    clinicId: appointmentData.clinic?.id,
    appointmentStartDate: new Date(appointmentData.startDate),
    appointmentEndDate: appointmentData.endDate
      ? new Date(appointmentData.endDate)
      : undefined
  });
  const statusTransition = getStatusTransition(
    appointmentData.status ?? APPOINTMENT_STATUS_UNCONFIRMED
  );
  const isPendingPatientVerification = isDraftPendingPatientVerification(
    appointmentData.status,
    Boolean(appointmentData.patient)
  );

  const [open, setOpen] = React.useState(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const changeAppointmentStatus = useCallback(
    async (sendCancellationMessage?: boolean) => {
      const appointment = isAvailable
        ? appointmentData
        : {
            ...appointmentData,
            shouldCreateScheduleForStaff: true
          };

      openPatientChartOnArrival(
        openPatientOnAppointmentStatusChange,
        statusTransition!.status,
        appointmentData?.patient?.id
      );

      await updateStatus(
        appointment,
        statusTransition!.status,
        sendCancellationMessage
      );
      dispatch(hidePreview());
    },
    [
      statusTransition,
      appointmentData,
      dispatch,
      updateStatus,
      isAvailable,
      openPatientOnAppointmentStatusChange
    ]
  );

  const rescheduleAppointment = useCallback(() => {
    const {
      // Reschedule always POSTs a NEW appointment — a carried-over `id` makes
      // the new appointment's form think it's editing an existing one,
      // wrongly locking the SMS/Email notification method checkboxes (SN-18966).
      id,
      status,
      outcome,
      outcome_notes,
      outcome_reason,
      diagnosis,
      hearing_test_conducted,
      chartNote,
      chartNoteId,
      ...appointment
    } = appointmentData;
    setupAppointmentModal(
      {
        ...appointment,
        // Backend marks the source appointment RESCHEDULED (and links
        // replacement_appointment / transfers intake form requests) as a
        // side effect of creating the new appointment with this set —
        // Appointment.save() in suno-backend. No separate status update needed.
        rescheduled_appointment_id:
          (appointmentData as unknown as { modelId?: number }).modelId ?? id
      },
      "add",
      () => {
        dispatch(endRescheduling());
      },
      undefined,
      // isRescheduling: without this, appointment-form.component.tsx's
      // "!eventId && !isRescheduling" default-notification effect overwrites
      // the carried-over should_notify/notification_method with patient
      // defaults (SN-18966).
      true
    );
    dispatch(startRescheduling(appointmentData));
  }, [setupAppointmentModal, appointmentData, dispatch]);

  const practice = useAppSelector(selectCurrentPractice);
  const asksMessageOnCancel =
    Boolean(appointmentData.patient) && asksCancellationMessage(practice);

  /**
   * The status confirmation. A cancel on a practice set to "Ask each time" also asks
   * "Send cancellation message?" here, and the answer rides with the status change — so
   * the outcome dialog that may follow does not ask it again.
   */
  const openStatusConfirm = useCallback(
    (
      status: number,
      onConfirm: (sendCancellationMessage?: boolean) => void
    ) => {
      const { title, content } = STATUS_CONFIRMATION_OPTIONS[status];
      dispatch(setTitle(title));
      if (status === APPOINTMENT_STATUS_CANCELED && asksMessageOnCancel) {
        const { content: withChoice, answer } = withCancellationMessageChoice(
          dispatch,
          {
            body: content,
            who: [
              appointmentData.patient?.first_name,
              appointmentData.patient?.last_name
            ]
              .filter(Boolean)
              .join(" ")
          }
        );
        dispatch(setContent(withChoice));
        dispatch(
          setOnChange(() => {
            const send = answer();
            if (send === undefined) return;
            onConfirm(send);
          })
        );
      } else {
        dispatch(setOnChange(() => onConfirm()));
        dispatch(setContent(content));
      }
      dispatch(setConfirmChangeDialogOpen(true));
    },
    [dispatch, asksMessageOnCancel, appointmentData.patient]
  );

  const handleAppointmentStatusChange = useCallback(() => {
    if (
      statusTransition!.status === APPOINTMENT_STATUS_COMPLETED &&
      !newAppointmentOutcomeChangesEnabled &&
      !canAppointmentBeCompleted
    ) {
      dispatch(setTitle("Are you sure you want to complete this appointment?"));
      dispatch(setOnChange(changeAppointmentStatus));
      dispatch(setConfirmChangeDialogOpen(true));
      dispatch(
        setContent(
          "Not all of the Appointment requirements were met. Do you still want to proceed?"
        )
      );
    } else if (
      Object.keys(STATUS_CONFIRMATION_OPTIONS).includes(
        statusTransition!.status.toString()
      )
    ) {
      openStatusConfirm(
        statusTransition!.status,
        (send) => void changeAppointmentStatus(send)
      );
    } else {
      changeAppointmentStatus();
    }
  }, [
    statusTransition,
    canAppointmentBeCompleted,
    changeAppointmentStatus,
    newAppointmentOutcomeChangesEnabled,
    dispatch,
    openStatusConfirm
  ]);

  const isAppointmentDateInTheFuture = getIsAppointmentDateInTheFuture(
    appointmentData.startDate
  );

  const isNextStatusTransitionDisabled =
    !appointmentData.patient ||
    (isAppointmentDateInTheFuture &&
      appointmentData.status === APPOINTMENT_STATUS_CONFIRMED) ||
    (hasCustomChecklist &&
      !canAppointmentBeCompleted &&
      statusTransition?.status === APPOINTMENT_STATUS_COMPLETED);

  const disabledNextStatuses = useMemo(() => {
    const result: number[] = [];

    if (isAppointmentDateInTheFuture) {
      result.push(APPOINTMENT_STATUS_NO_SHOW);

      if (appointmentData.status === APPOINTMENT_STATUS_UNCONFIRMED) {
        result.push(APPOINTMENT_STATUS_ARRIVED);
      }
    }

    return result;
  }, [isAppointmentDateInTheFuture, appointmentData.status]);

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      justifyContent="center"
    >
      <Grid item xs={12}>
        {statusTransition && (
          <StatusTransitionButtonGroup
            anchorRef={anchorRef}
            statusTransitionLabel={statusTransition.label}
            tooltipText={
              isNextStatusTransitionDisabled
                ? !appointmentData.patient
                  ? "Patient details needs to be verified and saved"
                  : hasCustomChecklist && !canAppointmentBeCompleted
                    ? "Complete all required checklist items to proceed"
                    : "Appointments that are scheduled for a future date cannot be marked as 'Patient Arrived'"
                : ""
            }
            isDisabled={isNextStatusTransitionDisabled}
            onAppointmentStatusChange={handleAppointmentStatusChange}
            onToggleDropDown={handleToggle}
          />
        )}
        <Popper
          sx={{
            zIndex: 1
          }}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom"
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="status-button-menu" autoFocusItem>
                    {STATUS_TRANSITION_OPTIONS.filter(
                      ({ validTransitionsFor, status }) =>
                        !isPendingPatientVerification &&
                        validTransitionsFor.includes(appointmentData.status) &&
                        !disabledNextStatuses.includes(status)
                    ).map((option) => (
                      <MenuItem
                        key={option.label}
                        onClick={() => {
                          const appointment = isAvailable
                            ? appointmentData
                            : {
                                ...appointmentData,
                                shouldCreateScheduleForStaff: true
                              };
                          const patientId = appointmentData?.patient?.id;
                          if (
                            option.status === APPOINTMENT_STATUS_RESCHEDULED
                          ) {
                            const { title, content } =
                              STATUS_CONFIRMATION_OPTIONS[option.status];
                            dispatch(setTitle(title));
                            dispatch(setOnChange(rescheduleAppointment));
                            dispatch(setConfirmChangeDialogOpen(true));
                            dispatch(setContent(content));
                          } else if (
                            Object.keys(STATUS_CONFIRMATION_OPTIONS).includes(
                              option.status.toString()
                            )
                          ) {
                            openStatusConfirm(option.status, (send) => {
                              openPatientChartOnArrival(
                                openPatientOnAppointmentStatusChange,
                                option.status,
                                patientId
                              );
                              updateStatus(appointment, option.status, send);
                              setOpen(false);
                              dispatch(hidePreview());
                            });
                          } else {
                            openPatientChartOnArrival(
                              openPatientOnAppointmentStatusChange,
                              option.status,
                              patientId
                            );
                            updateStatus(appointment, option.status);
                            setOpen(false);
                            dispatch(hidePreview());
                          }
                        }}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Grid>
    </Grid>
  );
}
